import { Badge, BlockStack, Grid, InlineStack, Link, Text, Tooltip, useBreakpoints } from '@shopify/polaris';
import HoverMarquee from 'components/HoverMarquee';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import helpers from 'helpers/index';
import { ENUM_TRIP_MANIFEST_STATUS, TypedTripManifest } from 'queries/trip_manifest.query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { useCurrentTicketMoving } from 'context/CurrentTicketMovingContext';
import { useNavigate } from 'react-router-dom';
import {
  ENUM_TICKET_TYPE,
  TypedTicket,
  useMutateTicketClone,
  useMutateTicketTemporaryDelete,
  useMutateTicketDetach,
  useMutateTicketMarkAsPriority,
  useMutateMoving_ticket_to_trip,
} from 'queries/ticket.query';

import { callStatusCallbackForDisplay } from './ticket_helper';
import { ENUM_ORDER_INVOICE_TYPE, ENUM_ORDER_PAYMENT_STATUS, TypedOrders, useMutateOrderRemove } from 'queries/orders.query';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamation,
  faParachuteBox,
  faPlusSquare,
  faTaxi,
  faPercent,
  faCreditCard,
  faTriangleExclamation,
  faLock,
  faArrowUpRightFromSquare,
  faDongSign,
  faPlane,
  faPlaneArrival,
  faPlaneDeparture,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from '../../../utils/dayjs';
import Capacity from 'components/Capacity';
import { formatFlightStatus, getFlightStatusType } from '../../../utils/flightStatus';

const divAddNewTicket = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
};

type ManifestTicketForDisplay = {
  order: TypedOrders;
  ticket: TypedTicket;
};

function useHash() {
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return hash;
}

export default function TicketInManifest({
  ticketData,
  orders,
  manifest,
  type = 'seat', // 'seat' cho vé, 'shipping' cho hàng hóa
  editButtonCallback,
  editTaxButtonCallback,

  payButtonCallback,
  onNewTicket,
}: {
  ticketData: bigint[]; /** bảng ticket_id, chỗ này cần match với manifest  */
  manifest: TypedTripManifest;
  orders: TypedOrders[];
  type?: string;
  editButtonCallback: (order_id: string) => void;
  editTaxButtonCallback: (order_code: string) => void;

  payButtonCallback: (order_id: string) => void;
  onNewTicket: (trip_manifest_id: any, type: string, position?: string[]) => void;
}) {
  const seatMap: string[] = String(manifest?.seating?.seating_available || ' ').split('|');
  const seatTotalChar = manifest?.seating?.seating_number;
  const seatAvailableCount = manifest?.seating?.seating_available_count;

  const manifestTickets = useMemo<ManifestTicketForDisplay[]>(() => {
    const requestedTicketIds = new Set(ticketData.map(String));

    return orders.flatMap((order) =>
      (order.ticket ?? []).filter((ticket) => requestedTicketIds.has(String(ticket.ticket_id))).map((ticket) => ({ order, ticket }))
    );
  }, [orders, ticketData]);

  /** extract rootSearch path ... */
  const rootSearchPath = window.location.search;
  let StringQuery: Record<string, any> = useMemo(() => {
    return helpers.ExtractUrl(rootSearchPath);
  }, [rootSearchPath]);

  const { addNotification } = useNotification();
  const { mutateAsync: movingTicketCallback, isPending: loadingMovingTicket } = useMutateMoving_ticket_to_trip();

  /** Vị trí ghế đã ngồi */
  /* Tự động highlight ghế nếu có hash trên URL */
  const hash = useHash();
  const highlight_for_a_ticket = useMemo(() => {
    return String(hash || ' ')
      .replace('#', '')
      .trim();
  }, [hash]);

  const [currentSeatActive, setCurrentSeatActive] = useState<string[]>([]);
  const [currentEmptyTicketToActive, setCurrentEmptyTicketToActive] = useState([]);
  const [topTicketByPosition, setTopTicketByPosition] = useState<Record<string, string>>({});

  const currentSeatActiveCallback = useCallback(
    (order_id: any) => {
      //!SECTION
      setCurrentEmptyTicketToActive([]);
      let orderData = orders.find((a) => String(a.id) === String(order_id));
      let allTicketID = orderData?.ticket?.map((o) => o.ticket_id);
      return setCurrentSeatActive(allTicketID);

      // setCurrentSeatActive((prev) => {
      //   let updated = [];

      //   if (isMultiSelect) {
      //     // Nếu đã có trong danh sách -> bỏ chọn, ngược lại -> thêm vào
      //     const exists = prev.find((t) => t === id);
      //     updated = exists ? prev.filter((t) => t !== id) : [...prev, id];
      //   } else {
      //     // Chọn duy nhất vé này
      //     updated = [id];
      //   }
      //   return updated;
      // });
    },
    [orders]
  );

  useEffect(() => {
    if (!highlight_for_a_ticket) return;
    currentSeatActiveCallback(highlight_for_a_ticket);
  }, [highlight_for_a_ticket, ticketData]);

  const searchAllTicketIDInThatOrder = useCallback(
    (order_id: string) => {
      // tìm tất thảy các ID của cái đơn hàng này
      let orderData = orders.find((a) => String(a.id) === String(order_id));
      let ticketFilter = orderData?.ticket?.filter((o) => o.markAsDeleted === 0);
      let allTicketID = ticketFilter?.map((o) => o.ticket_id);
      setCurrentSeatActive(allTicketID);
      // empty cac thang khac
      setCurrentEmptyTicketToActive([]);
      return allTicketID;
    },
    [currentSeatActive, orders]
  );

  /** Chọn ghế trống */
  const onEmptyTicketClickToggleSelected = (indexNum) => {
    // Kiểm tra xem item đã được chọn chưa
    if (currentEmptyTicketToActive.includes(indexNum)) {
      // Nếu đã chọn, loại bỏ khỏi mảng
      setCurrentEmptyTicketToActive(currentEmptyTicketToActive.filter((itemId) => itemId !== indexNum));
    } else {
      // Nếu chưa được chọn, thêm vào mảng
      setCurrentEmptyTicketToActive([...currentEmptyTicketToActive, indexNum]);
    }
    setCurrentSeatActive([]);
  };

  /** Nút book luôn phải ăn chắc là đã include vị trí vào ồi */
  const makeSureClickMustIncludeForBookingButton = (indexNum: string) => {
    // Nếu chưa được chọn, thêm vào mảng
    let clonecurrentEmptyTicketToActive = [...currentEmptyTicketToActive];
    if (!currentEmptyTicketToActive.includes(indexNum)) {
      clonecurrentEmptyTicketToActive = [...currentEmptyTicketToActive, indexNum];
      setCurrentEmptyTicketToActive(clonecurrentEmptyTicketToActive);
    }
    setCurrentSeatActive([]);
    return clonecurrentEmptyTicketToActive;
  };

  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  const { currentTicketMoving, setCurrentTicketMoving, clearCurrentTicketMoving } = useCurrentTicketMoving();

  /** Cancel ticket */
  const { showDeleteConfirm } = useDeleteConfirm();
  const { mutateAsync: cancelATicket } = useMutateTicketTemporaryDelete();
  const handleCancelTicket = useCallback(
    async (ticket_id) => {
      let confirmCancelTicket = await showDeleteConfirm(
        'Bạn có chắc muốn hủy vé này?',
        `Việc hủy vé có thể được thông báo cho khách hàng qua Zalo. Tài xế cũng có thể nhận được các thông báo tương tự.`
      );
      if (confirmCancelTicket) {
        try {
          await cancelATicket(ticket_id);
          setCurrentSeatActive([]);
        } catch (e) {}
      }
    },
    [StringQuery?.h]
  );

  /** Remove a orders */
  const { mutateAsync: removeOrder } = useMutateOrderRemove();
  const handleRemoveOrder = useCallback(
    async (order_id) => {
      let confirmCancelTicket = await showDeleteConfirm('Bạn có chắc muốn xóa bỏ đơn hàng này?', `Việc xóa đơn hàng sẽ không thể hoàn tác.`);
      if (confirmCancelTicket) {
        try {
          await removeOrder(order_id);
          setCurrentSeatActive([]);
        } catch (e) {}
      }
    },
    [removeOrder]
  );

  /** nhân đôi vé */
  const { mutateAsync: cloneATicket } = useMutateTicketClone();
  const handleCloneTicket = useCallback(async (___ticket_id) => {
    // clone ...
    try {
      let { ticket_id } = await cloneATicket(___ticket_id);
      setCurrentTicketMoving([ticket_id]);
    } catch (e) {}
  }, []);

  /** Đánh dấu/bỏ đánh dấu vé ưu tiên */
  const { mutateAsync: toggleTicketPriority, isPending: isTogglingPriority, variables: togglingPriorityTicketId } = useMutateTicketMarkAsPriority();
  const handleToggleTicketPriority = useCallback(
    async (ticket: TypedTicket) => {
      if (!ticket.ticket_id) return;

      try {
        await toggleTicketPriority(ticket.ticket_id);
        addNotification('info', Number(ticket.mark_as_priority) === 1 ? 'Đã bỏ đánh dấu vé ưu tiên.' : 'Đã đánh dấu vé ưu tiên.');
      } catch (error: any) {
        addNotification('error', error?.response?.data?.message || error?.message || 'Không thể cập nhật trạng thái ưu tiên.');
      }
    },
    [addNotification, toggleTicketPriority]
  );

  /** tách vé */
  const { mutateAsync: detachATicket } = useMutateTicketDetach();
  const handleDetactTicket = useCallback(async (ticket_id) => {
    try {
      await detachATicket(ticket_id);
      addNotification('info', 'Tách vé thành công!');
      setCurrentSeatActive([ticket_id]);
    } catch (e) {}
  }, []);

  /** Bấm nút di chuyển ... */
  const [seatLoading, setSeatLoading] = useState<string | null>(null);
  const moveTicketToHere = useCallback(
    async (position_seatCode: string) => {
      setSeatLoading(position_seatCode);
      await helpers.sleep(500); /** Không delay không được, nó load nhanh quá không nhìn thấy ... */

      try {
        let ticketToMove: string | number | undefined;

        // Lấy vé đầu tiên và bỏ nó ra khỏi context
        if (!currentTicketMoving || currentTicketMoving.length === 0) {
          addNotification('info', 'Không có vé nào để di chuyển hoặc đã bị hủy.');
          setSeatLoading(null);
          clearCurrentTicketMoving();
          return;
        }

        const newTickets = [...currentTicketMoving];
        ticketToMove = newTickets.shift();
        setCurrentTicketMoving(newTickets);

        if (!ticketToMove) {
          setSeatLoading(null);
          clearCurrentTicketMoving();
          return;
        }

        await movingTicketCallback({
          seat_position: position_seatCode,
          trip_manifest_id: manifest?.id,
          ticket_id: BigInt(ticketToMove),
        });

        if (newTickets.length === 0) {
          clearCurrentTicketMoving();
        }
      } catch (e) {
        console.error(e, 'MOVING_TICKET_ERROR');
      } finally {
        setSeatLoading(null);
      }
    },
    [StringQuery, ticketData, manifest, currentTicketMoving]
  );

  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */

  /** Phát hiện click ra ngoài */
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        // 👉 Click ngoài
        setCurrentSeatActive([]);
        setCurrentEmptyTicketToActive([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigate = useNavigate();

  const { mdDown } = useBreakpoints();

  const _renderTicket = (item: ManifestTicketForDisplay, index, highlight = false) => {
    const { order, ticket } = item;
    const flight = order?.flight_radar_to_order?.flight_radar;
    const flightStatusType = getFlightStatusType(flight?.status);
    const flightStatus = !flight
      ? { label: 'Chưa theo dõi chuyến bay', icon: faPlane, color: '#6b7280' }
      : {
          label: formatFlightStatus(flight.status),
          icon: flightStatusType === 'landed' ? faPlaneArrival : flightStatusType === 'en route' ? faPlaneDeparture : faPlane,
          color:
            flightStatusType === 'landed'
              ? '#15803d'
              : flightStatusType === 'cancelled'
                ? '#dc2626'
                : flightStatusType === 'delayed'
                  ? '#d97706'
                  : flightStatusType === 'unknown'
                    ? '#6b7280'
                    : '#2563eb',
        };
    const landingTime = flight?.estimated_landing_time || flight?.scheduled_landing_time;
    const flightLastUpdatedLabel = flight?.updatedAt
      ? dayjs(/^\d+$/.test(flight.updatedAt) ? Number(flight.updatedAt) : flight.updatedAt).format('HH:mm DD/MM/YYYY')
      : 'Chưa có';
    const scheduledLandingLabel = flight?.scheduled_landing_time ? dayjs(flight.scheduled_landing_time).format('HH:mm') : '--';
    const estimatedLandingLabel = flight?.estimated_landing_time ? dayjs(flight.estimated_landing_time).format('HH:mm') : '--';
    const flightStatusTone =
      flightStatusType === 'landed'
        ? 'success'
        : flightStatusType === 'en route'
          ? 'info'
          : flightStatusType === 'cancelled'
            ? 'critical'
            : flightStatusType === 'delayed'
              ? 'warning'
              : flightStatusType === 'scheduled'
                ? 'attention'
                : undefined;
    const flightSummary = flight
      ? `${flightStatus.label} · ${scheduledLandingLabel} (${estimatedLandingLabel}) · Từ ${flight.flight_from || '--'} · ${flight.flight || ''}`.trim()
      : '';
    const hasBankTransaction = !!(order?.transactions && order?.transactions.find((t) => t.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.BANK));
    const hasDebitTransaction = !!(
      order?.transactions &&
      order?.transactions.find((t) =>
        [
          ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_AGENT,
          ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_CUSTOMER,
          ENUM_TRANSACTION_PAYMENT_METHOD.THIRD_PARTY,
        ].includes(t.payment_method)
      )
    );
    const paidFull = Number(order?.total_paid) >= Number(order?.total_must_pay);
    const isSelected = currentSeatActive?.some((id) => String(id) === String(ticket.ticket_id));
    const isMoving = helpers.isArray(currentTicketMoving) && currentTicketMoving?.some((id) => String(id) === String(ticket.ticket_id));
    return (
      <div
        className={`
          ticket_element ${order?.payment_status === 1 ? ' ticket_paid' : ''}
         order_id_${ticket.order_id}
         ticket_index_${index}
          ${highlight ? ' actived' : ''}
          ${isSelected ? ' actived' : ''}
          ${isMoving ? ' ticket_highlight' : ''}
          `}
        key={index + '______ticketElement'}
        onClick={(e) => {
          // const isMultiSelect = e.ctrlKey || e.metaKey; // ✅ hỗ trợ cả Ctrl (Windows) và Cmd (Mac)
          currentSeatActiveCallback(ticket.order_id);
        }}
      >
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Tooltip content={`Ticket id ${ticket.ticket_id} - Order Code ${order?.order_code}`}>
              <Text as="h3" variant="headingLg">
                {order.pickup_time}
              </Text>
            </Tooltip>
            <div className="ticket_badge_header_top">
              <InlineStack gap="200" align="end" blockAlign="center">
                {order?.is_locked === 1 ? <FontAwesomeIcon size="lg" icon={faLock} color="var(--p-color-icon-emphasis)" /> : null}

                {Number(ticket.mark_as_priority) === 1 ? (
                  <Tooltip content="Ghế ưu tiên">
                    <span role="img" aria-label="Ghế ưu tiên" tabIndex={0}>
                      <FontAwesomeIcon size="lg" icon={faStar} color="#f59e0b" />
                    </span>
                  </Tooltip>
                ) : null}

                <Tooltip content={`Hóa đơn: ${order?.invoices ? 'Đã tạo, số ' + order?.invoices?.shDon : 'Chưa xuất'}`}>
                  <FontAwesomeIcon
                    size={'lg'}
                    icon={faArrowUpRightFromSquare}
                    color={
                      order?.invoices
                        ? order?.invoices?.is_signed
                          ? 'var(--p-color-icon-emphasis)'
                          : 'var(--p-color-icon-critical)'
                        : 'var(--p-color-icon-secondary)'
                    }
                  />
                </Tooltip>

                <Tooltip
                  content={
                    flight ? (
                      <BlockStack gap="100">
                        <Text as="p" fontWeight="semibold">
                          {flight.flight || 'Chuyến bay'} · {flightStatus.label}
                        </Text>
                        <Text as="p">Bay từ: {flight.flight_from || '--'}</Text>
                        <Text as="p">Hạ cánh: {landingTime ? dayjs(landingTime).format('HH:mm DD/MM/YYYY') : '--'}</Text>
                        <Text as="p">Cập nhật: {flightLastUpdatedLabel}</Text>
                      </BlockStack>
                    ) : (
                      flightStatus.label
                    )
                  }
                >
                  <span role="img" aria-label={flightStatus.label} tabIndex={0}>
                    <FontAwesomeIcon size="lg" icon={flightStatus.icon} color={flightStatus.color} />
                  </span>
                </Tooltip>

                {order?.order_invoice_info ? (
                  <Tooltip content={order?.order_invoice_info?.tax_type}>
                    <FontAwesomeIcon
                      size={'lg'}
                      icon={faPercent}
                      color={
                        order?.order_invoice_info?.tax_type === ENUM_ORDER_INVOICE_TYPE.COMPANY
                          ? 'var(--p-color-icon-critical)'
                          : 'var(--p-color-icon-emphasis)'
                      }
                    />
                  </Tooltip>
                ) : (
                  <Tooltip content="Thuế mặc định">
                    <FontAwesomeIcon size={'lg'} icon={faPercent} color="var(--p-color-icon-secondary)" />
                  </Tooltip>
                )}

                {callStatusCallbackForDisplay(
                  order?.order_meta?.call_status,
                  order?.order_meta?.call_last_updated_author_data?.display_name,
                  order?.order_meta?.call_last_updated,
                  order?.order_call_log
                )}

                {order?.order_meta?.pickup_by_data ? (
                  <Tooltip
                    content={`Đón bởi ${order?.order_meta?.pickup_by_data?.display_name} lúc ${dayjs(Number(order?.order_meta?.pickup_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}`}
                  >
                    <FontAwesomeIcon size={'lg'} icon={faTaxi} color="var(--p-color-icon-emphasis)" />
                  </Tooltip>
                ) : (
                  <Tooltip content="Đang chờ đón">
                    <FontAwesomeIcon size={'lg'} icon={faTaxi} color="var(--p-color-icon-secondary)" />
                  </Tooltip>
                )}

                {order?.order_meta?.dropoff_by_data ? (
                  <Tooltip
                    content={`Trả bởi ${order?.order_meta?.dropoff_by_data?.display_name} lúc ${dayjs(Number(order?.order_meta?.dropoff_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}`}
                  >
                    <FontAwesomeIcon size={'lg'} icon={faParachuteBox} color="var(--p-color-icon-emphasis)" />
                  </Tooltip>
                ) : (
                  <Tooltip content="Chưa xuống xe">
                    <FontAwesomeIcon size={'lg'} icon={faParachuteBox} color="var(--p-color-icon-secondary)" />
                  </Tooltip>
                )}

                {order?.payment_status !== ENUM_ORDER_PAYMENT_STATUS.UNPAID ? (
                  <Tooltip
                    content={`Đã thanh toán ${helpers.formatNumberWithCommas(order?.total_paid)} / ${helpers.formatNumberWithCommas(order?.total_must_pay)}`}
                  >
                    {hasDebitTransaction ? (
                      <FontAwesomeIcon size={'lg'} icon={faTriangleExclamation} color="var(--p-color-icon-critical)" />
                    ) : hasBankTransaction ? (
                      <FontAwesomeIcon
                        size={'lg'}
                        icon={faCreditCard}
                        color={paidFull ? 'var(--p-color-icon-emphasis)' : 'var(--p-color-icon-critical)'}
                      />
                    ) : (
                      <FontAwesomeIcon
                        size={'lg'}
                        icon={faDongSign}
                        color={paidFull ? 'var(--p-color-icon-emphasis)' : 'var(--p-color-icon-critical)'}
                      />
                    )}
                  </Tooltip>
                ) : (
                  <Tooltip content="Chưa thanh toán">
                    <FontAwesomeIcon size={'lg'} icon={faDongSign} color="var(--p-color-icon-secondary)" />
                  </Tooltip>
                )}
              </InlineStack>
            </div>
          </InlineStack>
          <BlockStack gap="050" inlineAlign="end">
            <div className="ticket_address">
              <HoverMarquee speed={80}>{order?.pickup_address}</HoverMarquee>
            </div>
            {order?.pickup_note ? (
              <Tooltip content={order?.pickup_note}>
                <div className="ticket_address_note">{helpers.trimContentString(order?.pickup_note, 40)}</div>
              </Tooltip>
            ) : null}
            <div className="ticket_address">
              <HoverMarquee speed={80}>{order?.dropoff_address}</HoverMarquee>
            </div>
            {order?.dropoff_note ? (
              <Tooltip content={order?.dropoff_note}>
                <div className="ticket_address_note">{helpers.trimContentString(order?.dropoff_note, 40)}</div>
              </Tooltip>
            ) : null}
          </BlockStack>
          <InlineStack align="space-between" blockAlign="start" gap="100">
            <BlockStack gap="050">
              {order.pickup_contact_phonenumber ? (
                <Text as="p" fontWeight="bold">
                  {order.pickup_contact_name} / ({order?.pickup_contact_phonenumber})
                </Text>
              ) : null}

              <Text as="p" fontWeight="bold">
                <Link monochrome removeUnderline onClick={() => navigate('/customer/view/' + order?.customer_data?.user_id)}>
                  {order?.customer_data?.display_name} <br /> {order?.customer_data?.user_phonenumber}
                </Link>
              </Text>
            </BlockStack>
            <div>
              <Text as="p" fontWeight="bold" alignment="end">
                {__helpers.formatNumberWithCommas(ticket.price - ticket.discount)}
              </Text>
              <Text as="p" alignment="end">
                Đơn: {__helpers.formatNumberWithCommas(order?.total_must_pay)} / Phí: {__helpers.formatNumberWithCommas(order?.total_fee)}
              </Text>
            </div>
          </InlineStack>
          {order?.note ? (
            <Tooltip content={order?.note || 'Không có ghi chú!'}>
              <Text as="p" variant="bodySm" tone="subdued">
                Ghi chú: {order?.note}
              </Text>
            </Tooltip>
          ) : (
            <Text as="p" variant="bodySm" tone="subdued">
              Ghi chú: --
            </Text>
          )}
          {flight ? (
            <div
              style={{
                color: 'var(--p-color-text-success)',
                fontSize: '12px',
                fontWeight: 700,
                width: '100%',
              }}
            >
              <button
                type="button"
                title="Click để sao chép thông tin chuyến bay"
                aria-label="Sao chép thông tin chuyến bay"
                style={{
                  appearance: 'none',
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  color: 'inherit',
                  font: 'inherit',
                  cursor: 'copy',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                  textAlign: 'left',
                }}
                onClick={async (event) => {
                  event.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(flightSummary);
                    addNotification('info', 'Đã sao chép thông tin chuyến bay.');
                  } catch (error) {
                    addNotification('error', 'Không thể sao chép thông tin chuyến bay.');
                  }
                }}
              >
                <Badge tone={flightStatusTone}>{flightStatus.label}</Badge>
                <span>
                  {scheduledLandingLabel} ({estimatedLandingLabel}) · Từ {flight.flight_from || '--'} · {flight.flight}
                </span>
              </button>
            </div>
          ) : null}
        </BlockStack>
        <br />

        <br />
        <br />
        <br />
        <div className="toolchain">
          <button
            onClick={(e) => {
              e.stopPropagation();
              editButtonCallback(order.id);
            }}
            title="Cập nhật lại vé"
          >
            U
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              editTaxButtonCallback(order.id);
            }}
            title="Cập nhật thông tin thuế"
          >
            T
          </button>
          <Capacity
            current_user_can={'manage_driver_settlement'}
            extra_condition={
              !hasBankTransaction &&
              !hasDebitTransaction &&
              !order?.order_invoice_info &&
              !order?.invoices &&
              manifest.trip_status > ENUM_TRIP_MANIFEST_STATUS.COMPLETED
            }
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveOrder(order.id);
              }}
              title="Xoá đơn hàng"
            >
              R
            </button>
          </Capacity>

          {Number(order?.is_locked) !== 1 || Number(manifest?.trip_status) < ENUM_TRIP_MANIFEST_STATUS.FINALIZED ? (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  let allTicketSelected = searchAllTicketIDInThatOrder(ticket.order_id);
                  // currentSeatActiveCallback(ticket.order_id);
                  setCurrentTicketMoving(allTicketSelected);
                }}
                title="Di chuyển vé"
              >
                M
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelTicket(ticket.ticket_id);
                }}
                title="Hủy vé"
              >
                D
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloneTicket(ticket.ticket_id);
                }}
                title="Nhân đôi vé"
              >
                C
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void handleToggleTicketPriority(ticket);
                }}
                type="button"
                title={Number(ticket.mark_as_priority) === 1 ? 'Bỏ đánh dấu ưu tiên' : 'Đánh dấu ưu tiên'}
                aria-pressed={Number(ticket.mark_as_priority) === 1}
                disabled={isTogglingPriority && String(togglingPriorityTicketId) === String(ticket.ticket_id)}
                style={Number(ticket.mark_as_priority) === 1 ? { backgroundColor: '#f59e0b', color: '#ffffff' } : undefined}
              >
                K
              </button>

              {order.total_seat > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDetactTicket(ticket.ticket_id);
                  }}
                  title="Tách vé"
                >
                  S
                </button>
              )}
            </>
          ) : null}

          {/* {hasDebitTransaction ? ( */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              payButtonCallback(ticket.order_id);
            }}
            title="Thanh toán cho đơn này"
          >
            P
          </button>
        </div>
        <div className="ticket_author">
          <Text as="p" alignment="end">
            {order?.author_data?.display_name}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <div ref={wrapperRef}>
      {type === 'seat' && (
        <Grid>
          {Array.from({ length: seatTotalChar }, (_, i) => i + 1).map((index) => {
            const position = seatMap[index - 1] || 'no_where';
            const ticketsAtPosition = manifestTickets.filter(({ ticket }) => ticket.seat_position === position);
            const topTicketId = topTicketByPosition[position];
            const stackedTickets = topTicketId
              ? [...ticketsAtPosition].sort(({ ticket: ticketA }, { ticket: ticketB }) => {
                  if (String(ticketA.ticket_id) === topTicketId) return 1;
                  if (String(ticketB.ticket_id) === topTicketId) return -1;
                  return 0;
                })
              : ticketsAtPosition;
            if (index > seatAvailableCount)
              return (
                <Grid.Cell key={index} columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                  <div className="ticket_element disabled">
                    <Text as="p">
                      <FontAwesomeIcon icon={faExclamation} /> Ghế không được phép đặt
                    </Text>
                  </div>
                </Grid.Cell>
              );
            const isMoving = helpers.isArray(currentTicketMoving) && currentTicketMoving.length > 0;

            return (
              <Droppable
                droppableId={`add_passenger_ticket:position_${position}:manifest_item_${manifest?.id}`}
                key={position + '_add_passenger_ticket'}
                type="SUB_ITEM"
                isDropDisabled={ticketsAtPosition.length > 0 || !!mdDown || manifest.trip_status === ENUM_TRIP_MANIFEST_STATUS.FINALIZED}
              >
                {(provided, snapshot) => {
                  return (
                    <Grid.Cell key={index} columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {stackedTickets.length > 0 ? (
                          <div
                            className={`ticket-card-stack ${stackedTickets.length > 1 ? 'ticket-card-stack--multi' : ''}`}
                            style={{ height: `${(stackedTickets.length > 1 ? 210 : 245) + (stackedTickets.length - 1) * 20}px` }}
                          >
                            {stackedTickets.map((tmt, stackIndex) => (
                              <Draggable
                                key={`ticket_id_` + tmt.ticket.ticket_id}
                                draggableId={`ticket_id_` + tmt.ticket.ticket_id}
                                index={stackIndex}
                                isDragDisabled={!!mdDown || isMoving || manifest.trip_status === ENUM_TRIP_MANIFEST_STATUS.FINALIZED}
                              >
                                {(provided, draggableSnapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`ticket-card-stack__layer ${
                                      draggableSnapshot.isDragging || draggableSnapshot.isDropAnimating ? 'ticket-card-stack__layer--dragging' : ''
                                    }`}
                                    style={{
                                      ...provided.draggableProps.style,
                                      ...(draggableSnapshot.isDragging || draggableSnapshot.isDropAnimating
                                        ? {}
                                        : {
                                            top: `${stackIndex * 20}px`,
                                            left: `${stackIndex * 10}px`,
                                            right: `${(stackedTickets.length - stackIndex - 1) * 10}px`,
                                          }),
                                      zIndex: stackIndex + 1,
                                    }}
                                    onClick={() =>
                                      setTopTicketByPosition((current) => ({
                                        ...current,
                                        [position]: String(tmt.ticket.ticket_id),
                                      }))
                                    }
                                  >
                                    {stackIndex === stackedTickets.length - 1 && stackedTickets.length > 1 ? (
                                      <span className="ticket-card-stack__count">{stackedTickets.length} vé</span>
                                    ) : null}
                                    {_renderTicket(tmt, position, snapshot.isDraggingOver)}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          </div>
                        ) : (
                          <div
                            className={`ticket_element clickable ${currentEmptyTicketToActive?.includes(position) ? ' actived' : ''} 
                              ${snapshot.isDraggingOver ? ' actived' : ''}
                              ${!helpers.isEmpty(currentTicketMoving) ? (currentTicketMoving.length <= manifest.seating.seating_available_count - manifest.total_occupied_seats ? ' ticket_moving_selectable' : 'ticket_moving_unselectable') : ''}
                              ${seatLoading !== null && seatLoading === position ? ' loading' : ''}
                              `}
                            onClick={() => {
                              if (
                                !helpers.isEmpty(currentTicketMoving) &&
                                currentTicketMoving.length <= manifest.seating.seating_available_count - manifest.total_occupied_seats
                              ) {
                                moveTicketToHere(position);
                              } else {
                                onEmptyTicketClickToggleSelected(position);
                              }
                            }}
                          >
                            <div style={divAddNewTicket}>
                              <span>Ghế trống</span>
                              {manifest.trip_status < ENUM_TRIP_MANIFEST_STATUS.FINALIZED ? (
                                <div className="toolchain">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const currentHoldPosition = makeSureClickMustIncludeForBookingButton(position); // bắt buộc ...
                                      onNewTicket(manifest.id, ENUM_TICKET_TYPE.PASSENGER, currentHoldPosition);
                                      setCurrentEmptyTicketToActive([]);
                                    }}
                                  >
                                    B
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {/* {provided.placeholder} */}
                      </div>
                    </Grid.Cell>
                  );
                }}
              </Droppable>
            );
          })}
        </Grid>
      )}

      {type === 'shipping' && (
        <Droppable
          droppableId={`add_shipping_ticket:position_trunk:manifest_item_${manifest?.id}`}
          key={manifest.id + 'add_shipping_ticket'}
          type="SUB_ITEM"
          isDropDisabled={!!mdDown || manifest.trip_status > ENUM_TRIP_MANIFEST_STATUS.FINALIZED}
        >
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <Grid>
                {manifestTickets.map((tmt, index) => {
                  return (
                    <Draggable
                      key={`ticket_id_` + tmt.ticket.ticket_id}
                      draggableId={`ticket_id_` + tmt.ticket.ticket_id}
                      index={index}
                      isDragDisabled={!!mdDown}
                    >
                      {(provided) => (
                        <Grid.Cell key={`grid_` + tmt.ticket.ticket_id} columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            {_renderTicket(tmt, index + 111)}
                          </div>
                        </Grid.Cell>
                      )}
                    </Draggable>
                  );
                })}
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                  {manifest.trip_status < ENUM_TRIP_MANIFEST_STATUS.FINALIZED ? (
                    <div
                      className={`ticket_element ${snapshot.isDraggingOver ? 'actived' : ''}
                      ${snapshot.isDraggingOver ? ' actived' : ''}
                      ${!helpers.isEmpty(currentTicketMoving) ? ' ticket_moving_selectable' : ''}
                      ${seatLoading !== null && seatLoading === 'trunk' ? ' loading' : ''}
                    `}
                      onClick={() => {
                        if (!helpers.isEmpty(currentTicketMoving)) {
                          moveTicketToHere('trunk');
                        } else {
                          onNewTicket(manifest.id, ENUM_TICKET_TYPE.SHIPMENT, ['trunk']);
                        }
                      }}
                    >
                      <div style={divAddNewTicket}>
                        <Text as="p">
                          <FontAwesomeIcon icon={faPlusSquare} />
                          <span>Thêm hàng</span>
                        </Text>
                      </div>
                    </div>
                  ) : (
                    <div className={`ticket_element`}>
                      <div style={divAddNewTicket}>
                        <Text as="p">
                          <FontAwesomeIcon icon={faPlusSquare} />
                          <span>Thêm hàng</span>
                        </Text>
                      </div>
                    </div>
                  )}

                  {/* {provided.placeholder} */}
                </Grid.Cell>
              </Grid>
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
}

